<template>
  <div
    class="product-outfits grid"
  >
    <div class="col xs-span l6-59">
      <div class="product-outfits__header">
        <h4
          class="product-outfits__heading text-heading-s font-family-freight"
          :class="outfitsData?.length > 4 && 'col xs-span l6-59'"
          v-text="headingText"
        />

        <p
          class="product-outfits__subheading text-heading-content-5xs"
          :class="outfitsData?.length > 4 && 'col xs-span l6-59'"
          v-text="subheadingText"
        />
      </div>

      <splide
        :key="loadedCount"
        ref="lbsplide"
        class="lookbook-images__carousel"
        :has-track="false"
        :options="localConfig"
        @splide:moved="handleArrowVisibility"
      >
        <splide-track>
          <splide-slide
            v-for="(outfit, index) of outfitsData"
            :key="`outfit-${index}`"
          >
            <shop-the-look
              :key="outfit.handle"
              :source="product.handle"
              :outfit="outfit"
              :outfit-index="index"
              :main-product="product"
              :slide-index="activeSlideIndex"
              :class="{
                [$class('active')] : activeSlideIndex !== null ?
                  activeSlideIndex === index : getActiveOutfitIndex === index
              }"
              @update-active-slide="updateActiveSlide"
            />
          </splide-slide>
        </splide-track>
        <div class="lookbook-images__arrows splide__arrows">
          <button
            class="splide__arrow splide__arrow--prev"
            type="button"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.previous')"
            />
            <icon-arrow-left />
          </button>
          <button
            ref="nextArrow"
            class="splide__arrow splide__arrow--next"
            type="button"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.next')"
            />
            <icon-arrow-right />
          </button>
        </div>
      </splide>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product outfits (product-outfits)
 * -----------------------------------------------------------------------------
 * Product outfits and shop the look section.
 *
 * @param {Number} currentIndex - The current product image index
 * @param {Array} outfitKeys - Array of outfits to load.
 * @param {Object} product - The product data
 *
 */
import { mapState } from 'vuex'
import IconArrowLeft from '~icons/directional-navigation/arrow/left-small.svg'
import IconArrowRight from '~icons/directional-navigation/arrow/right-small.svg'
import ShopTheLook from '~async/shop-the-look/shop-the-look'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'

import { values as breakpoints } from '~/config/breakpoints'
import outfitQuery from '~graphql/outfit.gql'
import { formatOutfit } from '~/format/format-outfit'

export default {
  name: 'ProductOutfits',

  components: {
    IconArrowLeft,
    IconArrowRight,
    ShopTheLook,
    Splide,
    SplideSlide,
    SplideTrack,
  },

  props: {
    currentIndex: {
      type: Number,
      default: 0,
      required: true,
    },
    outfitKeys: {
      type: Array,
      default: () => [],
      required: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-current-index': () => true,
  },

  data() {
    return {
      localConfig: {},
      activeSlideIndex: null,
      outfitsData: [],
      loadedCount: 0,
    }
  },
  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * The index of the current outfit thumbnail
     * @returns {Number}
     */
    currentImageWithOutfitIndex() {
      let imageIndex = null

      this.imagesWithOutfits.forEach((image, index) => {
        if (image.outfit === this.currentOutfit) {
          imageIndex = index
        }
      })

      return imageIndex
    },

    getActiveOutfitIndex() {
      let activeIndex = null

      this.imagesWithOutfits.forEach((image, index) => {
        if (image.outfit === this.currentOutfit) {
          activeIndex = index
        }
      })
      return activeIndex
    },

    /**
     * Compute the section's heading.
     * @returns {String} Heading
     */
    headingText() {
      return this.$string('shop_the_look.heading_no_title')
    },

    /**
     * Compute the section's subheading.
     * @returns {String} subheading
     */
    subheadingText() {
      return this.$string(
        'shop_the_look.subheading',
        {
          replace: { product: this.product?.title || '' },
        },
      )
    },

    /**
     * The current outfit key
     * @returns {String|null}
     */
    currentOutfit() {
      if (!this.outfitKeys?.length) {
        return null
      }

      // Look for an outfit for the current image
      let key = this.outfitKeys[this.currentIndex] || null

      // If none is found, look at previous images until a fallback is found
      if (!key && this.currentIndex > 0) {
        for (let index = this.currentIndex; index >= 0; index--) {
          if (this.outfitKeys[index]) {
            key = this.outfitKeys[index]
            break
          }
        }
      }

      // If there is still no outfit, look at next images for a fallback
      if (!key && this.currentIndex < this.outfitKeys.length) {
        for (let index = this.currentIndex; index < this.outfitKeys.length; index++) {
          if (this.outfitKeys[index]) {
            key = this.outfitKeys[index]
            break
          }
        }
      }

      return key
    },

    /**
     * The product images that have outfits
     * @returns {Array}
     */
    imagesWithOutfits() {
      const outfitsFound = []
      const images = []

      this.product?.media?.forEach(({ image }, index) => {
        const altText = image?.raw_alt || image?.alt

        if (!altText) {
          return
        }

        let outfit = null
        const firstSplit = altText?.split('{{ outfit:')
        const secondSplit = firstSplit?.[1]?.split(' }}')

        if (!secondSplit?.[1]) {
          return
        }

        outfit = secondSplit[0].trim()

        if (!outfitsFound.includes(outfit)) {
          outfitsFound.push(outfit)
          images.push({
            index,
            image,
            outfit,
          })
        }
      })

      return images
    },

    /**
     * The next outfit thumbnail index
     * @returns {Number}
     */
    nextOutfit() {
      if (this.currentImageWithOutfitIndex < this.imagesWithOutfits.length - 1) {
        return this.currentImageWithOutfitIndex + 1
      }

      return this.imagesWithOutfits.length - 1
    },

    /**
     * The previous outfit thumbnail index
     * @returns {Number}
     */
    previousOutfit() {
      if (this.currentImageWithOutfitIndex > 0) {
        return this.currentImageWithOutfitIndex - 1
      }

      return 0
    },
  },

  watch: {
    outfitKeys: {
      handler() {
        this.getOutfits()
      },
    },

    async getActiveOutfitIndex(newIndex) {
      if (!this.isMobile && this.outfitsData?.length <= 2) {
        return
      }

      await this.$nextTick()
      this.$refs.lbsplide?.splide.go(newIndex)
    },
  },

  mounted() {
    this.handleArrowVisibility()
  },

  beforeMount() {
    this.getOutfits()
    this.updateLocalConfig()
  },

  methods: {

    updateLocalConfig() {
      const leftSpacing = '0'
      const rightSpacing = '0'
      this.localConfig = {
        type: 'slide',
        perPage: 2,
        direction: 'ltr',
        focus: 'left',
        drag: this.outfitsData?.length > 2,
        start: 0,
        gap: '8px',
        speed: this.$timing('normal'),
        arrows: this.outfitsData?.length > 2,
        pagination: this.outfitsData?.length > 2,
        trimSpace: false,
        padding: { left: leftSpacing, right: rightSpacing },
        breakpoints: {
          [breakpoints.l]: {
            perPage: 1,
            padding: { right: '0', left: '0' },
            start: 0,
            drag: this.outfitsData?.length > 1,
            arrows: this.outfitsData?.length > 1,
            pagination: this.outfitsData?.length > 1,
          },
        },
      }
    },

    async getOutfits() {
      const outfits = await Promise.all(
        [...new Set(this.outfitKeys)].map(async(handle) => {
          if (!handle) {
            return null
          }

          const outfit = await this.getOutfit(handle)

          if (!outfit) {
            return null
          }

          return {
            ...outfit,
            handle,
          }
        }),
      )

      this.outfitsData = outfits.filter((outfit) => outfit !== null)
      this.updateLocalConfig()
      this.loadedCount += 1
    },

    async getOutfit(handle) {
      const data = await cnvs.Query({
        query: outfitQuery,
        variables: {
          country: this.$variable('store.country'),
          handle: {
            handle,
            type: 'outfit',
          },
          language: this.$variable('store.language'),
        },
      })

      return formatOutfit(data)
    },

    updateActiveSlide(index) {
      this.activeSlideIndex = index
    },
    handleArrowVisibility(splide, newIndex) {
      if (!this.isMobile) {
        const maxindex = this.$refs.lbsplide?.splide.length - 2 || 0
        if (newIndex >= maxindex) {
          this.$refs.nextArrow.style.display = 'none'
        } else {
          this.$refs.nextArrow.style.display = 'flex'
        }
      }
    },
  },
}
</script>

<style lang="scss">
@import './product-outfits';
</style>
